// @flow
/* eslint-disable no-underscore-dangle */
import React from 'react'
import type { ComponentType, ElementConfig } from 'react'
import type { GetColorFn } from './withPalette'
import type { StyleObject } from 'styletron-standard'
import type { StyletronComponent } from 'styletron-react/src/types'
import {
  styled as originalStyled,
  withStyle as originalWithStyle,
  withTransform as originalWithTransform,
  withWrapper,
} from 'styletron-react'
import maybeMap from 'mz-utils/maybeMap'
import { PaletteContext } from './withPalette'

// Types
export type BaseProps = {
  $palette: GetColorFn,
}
type ExtractPropTypes = <T>(StyletronComponent<T>) => T
type MixedBaseComponent = {
  base: string | ComponentType<any>,
  mozClassName?: string[] | string | Function,
}
type StyledFn = {
  (string, StyleObject): StyletronComponent<{}>,
  (MixedBaseComponent, StyleObject): StyletronComponent<{}>,
  <Props>(
    string,
    (Props & BaseProps) => StyleObject
  ): StyletronComponent<Props>,
  <Props>(
    MixedBaseComponent,
    (Props & BaseProps) => StyleObject
  ): StyletronComponent<Props>,
  <Base: ComponentType<any>>(
    Base,
    StyleObject
  ): StyletronComponent<$Diff<ElementConfig<Base>, { className: any }>>,
  <Base: ComponentType<any>, Props>(
    Base,
    (Props & BaseProps) => StyleObject
  ): StyletronComponent<$Diff<ElementConfig<Base>, { className: any }> & Props>,
}
type WithStyleFn = {
  <Base: StyletronComponent<any>, Props>(
    Base,
    (Props & BaseProps) => StyleObject
  ): StyletronComponent<$Call<ExtractPropTypes, Base> & Props>,
  <Base: StyletronComponent<any>>(
    Base,
    StyleObject
  ): StyletronComponent<$Call<ExtractPropTypes, Base>>,
}

/**
 * Add prefix `moz-` to isolate all global class names
 * @param  {string} val
 * @return {strnig}
 */
const prefixMozioClass = (val: string) => `moz-${val}`

/**
 * Returns true if the given value is a react component
 * @param  {React.ElementType}  component
 * @return {Boolean}
 */
function isReactComponent(component) {
  return (
    typeof component === 'string' ||
    typeof component === 'function' ||
    !!(component.prototype && component.prototype.isReactComponent) ||
    !!component.render
  )
}

/**
 * Append `moz-` class names to the given one, using class creator
 * function or just an array of classes
 * @param  {string} existingClassName
 * @param  {Function|string[]|string} newClassCreator
 * @param  {Object} props
 * @return {string}
 */
const appendMozioClassName = (existingClassName, newClassCreator, props) => {
  const prefixedClass =
    typeof newClassCreator === 'function'
      ? maybeMap(newClassCreator(props), prefixMozioClass).join(' ')
      : maybeMap(newClassCreator, prefixMozioClass).join(' ')

  return `${prefixedClass} ${existingClassName || ''}`.trim() || undefined
}

/**
 * A custom `styled` implementation that wraps original `styled` function
 * but also adds some custom logic:
 * 1. It automatically injects `$palette` to the style function
 * 2. Instead of the first argument you can pass an object like
 *    `{ base: 'div', mozClassNames: ['input'] }`, which will automatically
 *    add `moz-input` class name to the resulting DOM element
 * @param  {Object|React.ElementType} mixedArg
 * @param  {StyleObject} styleArgs
 * @return {React.ElementType}
 */
const customStyled = (mixedArg, styleArgs) => {
  const { base, mozClassName = [] } = isReactComponent(mixedArg)
    ? { base: mixedArg, mozClassName: [] }
    : mixedArg

  const wrapper = (StyledComponent) =>
    React.forwardRef((props, ref) => {
      const { getColor } = React.useContext(PaletteContext)
      const className = appendMozioClassName(
        props.className,
        mozClassName,
        props
      )

      return (
        <StyledComponent
          {...props}
          $palette={getColor}
          className={className}
          ref={ref}
        />
      )
    })

  const typedBase = originalStyled(base, styleArgs)
  return withWrapper(typedBase, wrapper)
}

export const styled = ((customStyled: any): StyledFn)
export const withStyle = ((originalWithStyle: any): WithStyleFn)
export const withTransform = originalWithTransform
